/// <reference path="../../css.d.ts" />
"use client";
// @ubm/ui/components/button — UIArc `registry/components/button/button.tsx` @ 7238f98196b147a8d50ce467abcbad310d4d8414 (MIT, Elia Kuratli).
//
// LE moteur canonique des boutons (lot U1, 05/10/2026). Pression ressort, morph du libellé, chargement et peinture UIArc
// repris à l'identique (variantes de pression, phases de libellé, largeur ressort, gardes « ancre de couche » / inerte / mouvement
// réduit). Une seule adaptation de structure : le nœud animé n'est plus `motion.button` mais `motion.create(ButtonElement)` —
// le NOYAU L9 (`../../primitives/ButtonElement`) : href → <a>, asChild → Slot, ref, événements et props HTML transmis, `type`
// sûr "button", slots leading/trailing décoratifs. Deux exports, UN rendu :
//   · `ButtonCore` — moteur ET peinture canoniques (classes du module : variante, taille, accent de marque, forme), réemployé
//     par les façades historiques (`@ubm/ui` Button / IconButton, `@ubm/product-ui` PresentedButton) qui ne font que
//     traduire leur API (solid/outline/ghost × tone, default/primary/ghost/destructive) vers ces variantes. Leurs classes
//     historiques restent posées (crochets, compat) mais la peinture effective est celle-ci (module hors couche > utilitaires
//     Tailwind en couche). Morph UIArc ACTIF PAR DÉFAUT pour tous (bouton et lien) — arbitrage Root du 05/10 : seule la
//     structure Slot/asChild l'écarte (Slot exige un enfant unique) ;
//   · `Button` — l'API UIArc (`variant`, `size`, `loading`).
// Événements HTML : `onDrag` / `onDragStart` / `onDragEnd` / `onAnimationStart` gardent leur sémantique React DOM (DragEvent,
// AnimationEvent). Motion emploie les MÊMES noms pour ses callbacks de geste (PanInfo) et d'animation : ils sont donc remis au
// nœud par un pont interne (`htmlEvents`) que Motion ne lit pas, jamais confiés à Motion.
// Marqueur interne `data-ubm-canon` : posé sur chaque nœud peint par le canon, il retire AUTOMATIQUEMENT ce nœud des anciennes
// règles de peau (native-fields-apple) sans prop ni classe exigée des appelants.
// Chargement : le bouton reste focalisable (un bouton désactivé perdrait le focus clavier en pleine action) ; `aria-busy`,
// `aria-disabled`, le clic est refusé (`preventDefault`, gestionnaire du consommateur non appelé).

import { cloneElement, forwardRef, isValidElement, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { MouseEvent, ReactNode, Ref, RefObject } from "react";
import { AnimatePresence, animate, motion, useIsPresent, useMotionValue, useReducedMotionConfig as useReducedMotion } from "motion/react";
import type { TargetAndTransition, Variants } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
// Fondation UIArc (jetons --arc-*) embarquée par le composant : aucun import d'app ni fournisseur requis.
import "../../styles/foundation.css";
import { ButtonElement, type ButtonElementProps } from "../../primitives/ButtonElement";
import styles from "./button.module.css";
import { buttonCanonClassName, type ButtonSize, type ButtonVariant } from "./button-classes";

export type { ButtonSize, ButtonVariant };

export interface ButtonCoreProps extends ButtonElementProps {
  loading?: boolean;
  /** Morph UIArc du libellé (largeur ressort + phases floutées). Actif par défaut ; `false` rend les enfants tels quels. */
  morph?: boolean;
  /**
   * Avec `asChild` SEULEMENT : l'enfant unique (le transport, ex. le `Link` injecté par l'app) reçoit le CORPS canon — morph du
   * libellé et indicateur de chargement — à la place de ses enfants bruts. Même Slot, mêmes ref, classes et événements : un seul
   * nœud interactif, celui de l'enfant. Défaut `false` : `asChild` rend l'enfant tel quel (Radix, déclencheurs).
   */
  asChildContent?: boolean;
  /** Variante UIArc peinte (défaut `primary`). */
  variant?: ButtonVariant;
  /** Taille UIArc (défaut `md`). */
  size?: ButtonSize;
  /** Remplissage par l'accent (marque Foundry `--action` via `--arc-accent`) au lieu de l'encre — façades « tone primary ». */
  accent?: boolean;
  /** Forme : `pill` (rayon plein), `icon` (carré, sans marge interne — IconButton). */
  shape?: "pill" | "icon";
}

export type ButtonProps = Omit<ButtonCoreProps, "morph">;

/** Gestionnaires HTML dont Motion réutilise le NOM pour ses propres callbacks : transmis au nœud par le pont, jamais à Motion. */
type HtmlEventsHomonymesMotion = Pick<ButtonElementProps, "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart">;

/** Pont interne : le noyau L9 reçoit les événements HTML homonymes de Motion par `htmlEvents` (prop que Motion ne connaît pas). */
const ButtonElementPont = forwardRef<HTMLButtonElement, ButtonElementProps & { htmlEvents?: HtmlEventsHomonymesMotion }>(
  function ButtonElementPont({ htmlEvents, ...props }, ref) {
    return <ButtonElement ref={ref} {...props} {...htmlEvents} />;
  },
);

/** Le noyau L9, animable : motion lui passe ref, style et ses props HTML ; les props motion restent chez motion. */
const MotionButtonElement = motion.create(ButtonElementPont);

/** Icon buttons press a little deeper, wide buttons a little less, so every size reads as the same push. */
const pressVariants: Variants = {
  pressed: (button: RefObject<HTMLButtonElement | null>) => {
    const width = button.current?.offsetWidth ?? 0;
    return { scale: width > motionTokens.button.wideThreshold ? motionTokens.button.pressWide : width && width <= motionTokens.button.iconThreshold ? motionTokens.button.pressIcon : motionTokens.button.press, transition: { duration: motionTokens.duration.instant, ease: [...motionTokens.ease.standard] } };
  },
};

const rest: TargetAndTransition = { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" };
/** Text rises about .3em out of a soft blur; the outgoing label lifts away a little faster. */
const textIn: TargetAndTransition = { opacity: 0, y: motionTokens.button.textEnterY, filter: `blur(${motionTokens.blur.soft}px)` };
const textOut: TargetAndTransition = { opacity: 0, y: motionTokens.button.textExitY, filter: `blur(${motionTokens.blur.soft}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } };
const iconIn: TargetAndTransition = { opacity: 0, scale: motionTokens.button.iconScale, filter: `blur(${motionTokens.blur.subtle}px)` };
const iconOut: TargetAndTransition = { opacity: 0, scale: motionTokens.button.iconScale, filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } };
const fadeIn: TargetAndTransition = { ...rest, opacity: 0 };
const fadeOut: TargetAndTransition = { opacity: 0, transition: { duration: motionTokens.duration.instant } };
/** Scale rides the spring; opacity and blur tween so blur never overshoots below zero. */
const iconEnter = { ...motionTokens.spring.snappy, opacity: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.enter] }, filter: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.enter] } } as const;

/** A key for the label content: text plus element names, so a new label or icon crossfades while prop-only updates stay in place. */
function labelKey(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number" || typeof node === "bigint") return String(node);
  if (Array.isArray(node)) return node.map(labelKey).join("");
  if (!isValidElement(node)) return "";
  const type = node.type as string | { displayName?: string; name?: string };
  return `<${typeof type === "string" ? type : type?.displayName ?? type?.name ?? ""}>${labelKey((node.props as { children?: ReactNode }).children)}`;
}

/** Springs the slot to the natural width of the incoming label when it changes, so a new label never snaps the button's size.
 *  The outgoing label is popped out of flow at once, so it never holds the old width. Other resizes (a late web font, a parent reflow) jump straight to the new width, so nothing wobbles on first paint. */
function useMorphWidth(content: RefObject<HTMLElement | null>, key: string, reduced: boolean) {
  const width = useMotionValue<number | "auto">("auto");
  const lastKey = useRef(key), armedUntil = useRef(0);
  useLayoutEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    armedUntil.current = performance.now() + motionTokens.button.morphWindowMs;
  }, [key]);
  useEffect(() => {
    const node = content.current, slot = node?.parentElement;
    if (!node || !slot || typeof ResizeObserver === "undefined") return;
    let measured = false;
    const observer = new ResizeObserver(([entry]) => {
      const next = entry!.contentRect.width;
      if (!next || !measured || reduced || performance.now() > armedUntil.current) { measured = next > 0; width.jump(next || "auto"); delete slot.dataset.morphing; return; }
      slot.dataset.morphing = "";
      animate(width, next, { ...motionTokens.spring.morph, onComplete: () => { delete slot.dataset.morphing; } });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [content, reduced, width]);
  return width;
}

function LabelPhase({ children, icon, reduced, ref }: { children: ReactNode; icon: boolean; reduced: boolean; ref?: Ref<HTMLSpanElement> }) {
  const present = useIsPresent();
  return <motion.span ref={ref} className={styles.labelPhase} aria-hidden={present ? undefined : true} initial={reduced ? fadeIn : icon ? iconIn : textIn} animate={rest} exit={reduced ? fadeOut : icon ? iconOut : textOut} transition={reduced ? { duration: motionTokens.duration.instant } : icon ? iconEnter : { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{children}</motion.span>;
}

export const ButtonCore = forwardRef<HTMLButtonElement, ButtonCoreProps>(function ButtonCore(
  { className, loading = false, morph = true, variant = "primary", size = "md", accent = true, shape, disabled, children, onClick, asChild = false, asChildContent = false, onDrag, onDragStart, onDragEnd, onAnimationStart, ...props },
  ref,
) {
  const reduceMotion = useReducedMotion() ?? false;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  // Transport opt-in (`asChild` + `asChildContent`) : le libellé morphé est celui de l'enfant, pas l'élément transporteur.
  const transport = asChild && asChildContent && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const labelNode: ReactNode = transport ? transport.props.children : children;
  const key = labelKey(labelNode);
  const width = useMorphWidth(contentRef, key, reduceMotion);
  const setRefs = useCallback((node: HTMLButtonElement | null) => {
    buttonRef.current = node;
    if (typeof ref === "function") ref(node); else if (ref) ref.current = node;
  }, [ref]);
  // A trigger that anchors a menu, popover, or dialog keeps its rect still while pressed, so the layer never measures a scaled anchor.
  // Radix triggers (asChild) pass aria-haspopup or data-state through; the stylesheet repeats the guard.
  const popup = props["aria-haspopup"];
  const anchorsLayer = (popup !== undefined && popup !== false && popup !== "false") || props.role === "combobox" || (props as Record<string, unknown>)["data-state"] !== undefined;
  const inert = disabled || loading || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  // Classes de peinture : LA source unique server-safe (`button-classes.ts`), partagée avec le helper RSC `buttonVariants`.
  const classes = [buttonCanonClassName({ variant, size, accent, shape }), className].filter(Boolean).join(" ");
  const refuse = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault();
  const htmlEvents: HtmlEventsHomonymesMotion = { onDrag, onDragStart, onDragEnd, onAnimationStart };

  // Slot (asChild) exige un enfant unique : par défaut ni morph ni indicateur ; le chargement y reste sémantique (aria + clic
  // refusé). Avec `asChildContent`, le MÊME corps canon est rendu DANS l'enfant transporteur (cloné) : un seul nœud interactif.
  const loader = (
    <AnimatePresence initial={false}>
      {loading ? <motion.span key="loader" className={styles.loader} aria-hidden="true" initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: motionTokens.button.iconScale }} animate={{ opacity: 1, scale: 1 }} exit={reduceMotion ? fadeOut : { ...iconOut, scale: motionTokens.button.loaderExitScale }} transition={reduceMotion ? { duration: motionTokens.duration.instant } : iconEnter}><span className={styles.spinner} /></motion.span> : null}
    </AnimatePresence>
  );
  const canonBody = (content: ReactNode): ReactNode => morph ? (
    <>
      {loader}
      <motion.span className={[styles.labelSlot, loading ? styles.loadingLabel : ""].filter(Boolean).join(" ")} style={{ width }}>
        <span ref={contentRef} className={styles.labelContent}>
          <AnimatePresence mode="popLayout" initial={false}>
            <LabelPhase key={key} icon={!/\S/.test(key.replace(/<[^>]*>/g, ""))} reduced={reduceMotion}>{content}</LabelPhase>
          </AnimatePresence>
        </span>
      </motion.span>
    </>
  ) : loading ? (
    <>
      {loader}
      <span className={[styles.labelContent, styles.loadingLabel].join(" ")}>{content}</span>
    </>
  ) : content;
  const body = transport ? cloneElement(transport, undefined, canonBody(labelNode)) : asChild ? children : canonBody(children);

  return (
    <MotionButtonElement
      ref={setRefs}
      className={classes}
      disabled={disabled}
      asChild={asChild}
      htmlEvents={htmlEvents}
      data-ubm-canon=""
      aria-busy={loading || undefined}
      custom={buttonRef}
      variants={pressVariants}
      whileTap={reduceMotion || anchorsLayer || inert ? undefined : "pressed"}
      transition={motionTokens.spring.snappy}
      {...props}
      // Loading keeps the button focusable (a disabled button would drop keyboard focus mid-action) and swallows presses instead.
      aria-disabled={loading || props["aria-disabled"] || undefined}
      onClick={inert ? refuse : onClick}
    >
      {body}
    </MotionButtonElement>
  );
});

ButtonCore.displayName = "ButtonCore";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  // Upstream pose `tabIndex` 0 (focus au clic sous Safari) ; sur un lien désactivé, le -1 du noyau L9 doit rester maître.
  const tabIndex = props.tabIndex ?? (props.href === undefined && !props.asChild ? 0 : undefined);
  return <ButtonCore ref={ref} {...props} tabIndex={tabIndex} />;
});

Button.displayName = "Button";

export default Button;
